<template>
  <div>
    <MultiInvoiceSelectionBanner
      v-if="multiPayEnabled && selectedInvoiceIds.length > 0"
      :count="selectedInvoiceIds.length"
      :outstanding-cents="summary?.total_balance_cents ?? 0"
      :loading="summaryLoading"
      :apply-disabled="applyDisabled"
      :apply-disabled-reason="applyDisabledReason"
      @clear="clearSelection"
      @apply="openModal"
    />

    <CustomerInvoicesDatatable
      ref="datatable"
      :flat="flat"
      :address="address"
      :filters="filters"
      :source="source"
      :customer-id="customerId"
      :show-sub-customers="showSubCustomers"
      :multi-select="multiPayEnabled"
      :bulk-select-ids="selectedInvoiceIds"
      @update:bulk-select-ids="handleSelectionChange"
      @row-click="$emit('row-click', $event)"
      @view-invoice="$emit('view-invoice', $event)"
    >
      <template #actions>
        <slot name="actions"></slot>
      </template>
    </CustomerInvoicesDatatable>

    <PayMultipleInvoicesModal
      v-if="modalOpen && summary"
      :customer-id="customerId"
      :invoices="summary.invoices"
      :receivers-url="summary.receivers_url"
      @payment-success="handlePaymentSuccess"
      @hide-modal="modalOpen = false"
      @stale="refreshSummary"
    />
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import PayMultipleInvoicesModal, {
  SelectedInvoiceSummary
} from "@/AcceptPayment/PayMultipleInvoicesModal.vue";
import CustomerInvoicesDatatable from "./CustomerInvoicesDatatable.vue";
import MultiInvoiceSelectionBanner from "./MultiInvoiceSelectionBanner.vue";

interface PaymentSummary {
  total_balance_cents: number;
  receivers_url: string;
  invoices: SelectedInvoiceSummary[];
}

const props = withDefaults(
  defineProps<{
    source: string;
    customerId: number;
    filters?: any[];
    address?: object | null;
    showSubCustomers?: boolean;
    flat?: boolean;
  }>(),
  {
    filters: () => [],
    address: null,
    showSubCustomers: false,
    flat: false
  }
);

const emit = defineEmits<{
  (e: "row-click", event: any): void;
  (e: "view-invoice", event: any): void;
  (e: "payment-success"): void;
}>();

const store = useStore();
const datatable = ref<{ refresh?: () => void } | null>(null);
const selectedInvoiceIds = ref<string[]>([]);
const summary = ref<PaymentSummary | null>(null);
const summaryLoading = ref(false);
const modalOpen = ref(false);

const multiPayEnabled = computed(() =>
  store.getters.featureEnabledForTenant("multi_invoice_payments")
);
const applyDisabledReason = computed(() => {
  if (selectedInvoiceIds.value.length < 2) return "Select at least 2 invoices.";
  if (summary.value?.invoices?.some((invoice) => !invoice.payable))
    return "A selected invoice has no balance due.";
  return "";
});
const applyDisabled = computed(
  () => !!applyDisabledReason.value || !summary.value
);

async function refreshSummary() {
  if (selectedInvoiceIds.value.length < 2) {
    summary.value = null;
    return;
  }
  summaryLoading.value = true;
  try {
    summary.value = await store.dispatch("fetchInvoicePaymentSummary", {
      customerId: props.customerId,
      invoiceIds: selectedInvoiceIds.value
    });
  } catch (err) {
    summary.value = null;
  } finally {
    summaryLoading.value = false;
  }
}

const debouncedRefreshSummary = debounce(refreshSummary, 400);

function handleSelectionChange(ids: string[]) {
  selectedInvoiceIds.value = ids;
  debouncedRefreshSummary();
}

function clearSelection() {
  debouncedRefreshSummary.cancel();
  EventBus.$emit("clear-datatable-bulk-select");
  selectedInvoiceIds.value = [];
  summary.value = null;
}

async function openModal() {
  // Re-fetch right before opening so the modal always shows live balances;
  // cancel any in-flight debounced fetch so it can't clobber the fresh one.
  debouncedRefreshSummary.cancel();
  await refreshSummary();
  if (!summary.value || applyDisabled.value) return;
  modalOpen.value = true;
}

function handlePaymentSuccess() {
  modalOpen.value = false;
  clearSelection();
  datatable.value?.refresh?.();
  emit("payment-success");
}

function refresh() {
  datatable.value?.refresh?.();
}

defineExpose({ refresh });
</script>
